import { useEffect, useRef, useState } from "react";
import { CalendarCheck, Video, Clock, CheckCircle2 } from "lucide-react";
import * as api from "@/lib/api";
import CallCountdown, { nextCall } from "@/components/lab/CallCountdown";

const SCRIPT = "https://assets.calendly.com/assets/external/widget.js";
const fmt = (d) => new Date(`${d}T00:00:00`).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" });

function Upcoming({ items }) {
  if (!items.length) return null;
  const nxt = nextCall(items);
  return (
    <div className="space-y-2" data-testid="portal-appt-list">
      <div className="font-display text-sm font-bold tracking-wider text-[#f1d78a]">I TUOI APPUNTAMENTI</div>
      {items.map((a) => (
        <div key={a.id} className="rounded-2xl border border-[#d4af37]/30 bg-[#0f1d3a]/80 p-4" data-testid={`portal-appt-${a.id}`}>
          <div className="flex items-center gap-2 text-sm font-semibold capitalize"><CalendarCheck className="h-4 w-4 text-[#d4af37]" /> {fmt(a.date)}</div>
          <div className="mt-1 flex items-center gap-2 text-xs text-slate-300"><Clock className="h-3.5 w-3.5" /> ore {a.time}{a.duration ? ` · ${a.duration} min` : ""}</div>
          <div className="mt-3 flex flex-wrap gap-2">
            {a.meetLink ? (
              <a href={a.meetLink} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-[#d4af37] to-[#f1d78a] px-4 py-2 text-xs font-extrabold text-[#0a1428]" data-testid={`portal-appt-join-${a.id}`}><Video className="h-4 w-4" /> Partecipa alla videochiamata</a>
            ) : <span className="text-[11px] text-slate-400">Il link della videochiamata comparirà qui.</span>}
            {a.rescheduleUrl && <a href={a.rescheduleUrl} target="_blank" rel="noreferrer" className="rounded-xl border border-[#d4af37]/30 px-3 py-2 text-xs text-slate-200" data-testid={`portal-appt-reschedule-${a.id}`}>Sposta</a>}
            {a.cancelUrl && <a href={a.cancelUrl} target="_blank" rel="noreferrer" className="rounded-xl border border-white/10 px-3 py-2 text-xs text-slate-400" data-testid={`portal-appt-cancel-${a.id}`}>Annulla</a>}
          </div>
          {nxt?.id === a.id && <CallCountdown appt={a} compact />}
        </div>
      ))}
    </div>
  );
}

export default function PortalAppointments({ items = [], booking, patientId, onBooked }) {
  const box = useRef(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!booking?.url) return;
    const init = () => {
      if (!box.current || !window.Calendly) return;
      box.current.replaceChildren();
      window.Calendly.initInlineWidget({
        url: `${booking.url}?hide_gdpr_banner=1&background_color=0a1428&text_color=f5ecd0&primary_color=d4af37`,
        parentElement: box.current,
        prefill: { name: booking.name || "", email: booking.email || "" },
        utm: { utmSource: "patient_portal", utmMedium: "inline_embed", utmContent: patientId },
      });
    };
    let s = document.getElementById("calendly-widget-js");
    if (!s) { s = document.createElement("script"); s.id = "calendly-widget-js"; s.src = SCRIPT; s.async = true; s.onload = init; document.body.appendChild(s); }
    else if (window.Calendly) init(); else s.addEventListener("load", init);
    const onMsg = (e) => {
      if (e.origin !== "https://calendly.com" || e.data?.event !== "calendly.event_scheduled") return;
      setDone(true);
      const tick = (n) => api.portalAppointmentsSync().catch(() => {}).finally(() => { onBooked(); if (n > 0) setTimeout(() => tick(n - 1), 20000); });
      setTimeout(() => tick(3), 3000);
    };
    window.addEventListener("message", onMsg);
    return () => { window.removeEventListener("message", onMsg); s.removeEventListener("load", init); };
  }, [booking?.url, booking?.name, booking?.email, patientId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-4" data-testid="portal-appointments">
      <Upcoming items={items} />
      {done && <div className="flex items-center gap-2 rounded-2xl border border-[#d4af37]/40 bg-[#d4af37]/10 p-4 text-sm text-[#f1d78a]" data-testid="portal-appt-booked"><CheckCircle2 className="h-5 w-5" /> Appuntamento prenotato! Lo vedrai qui sopra entro pochi istanti.</div>}
      <div className="font-display text-sm font-bold tracking-wider text-[#f1d78a]">PRENOTA CON IL COACH</div>
      <div ref={box} className="overflow-hidden rounded-2xl border border-[#d4af37]/25 bg-[#0a1428]" style={{ minWidth: 300, height: 720 }} data-testid="portal-calendly-widget" />
    </div>
  );
}
